/** PostHog's browser session contract, without loading the browser SDK. */
export const SESSION_IDLE_MS = 30 * 60 * 1000;
export const SESSION_MAX_MS = 24 * 60 * 60 * 1000;
export const POSTHOG_SESSION_COOKIE = "tabbio_phsession";
export type BrowserSession = Readonly<{
  id: string;
  startedAt: number;
  lastActivityAt: number;
}>;
export type SessionStorage = {
  read(): string | null;
  write(value: string | null): void;
};

export function uuidV7Timestamp(id: string): number | null {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    id,
  )
    ? parseInt(id.slice(0, 8) + id.slice(9, 13), 16)
    : null;
}

export function uuidV7(now = Date.now()): string {
  const bytes = new Uint8Array(16);
  globalThis.crypto.getRandomValues(bytes);
  let time = now;
  for (let index = 5; index >= 0; index--) {
    bytes[index] = time % 256;
    time = Math.floor(time / 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x70;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function createBrowserSession(options: {
  storage: SessionStorage;
  now?: () => number;
}) {
  const now = options.now ?? Date.now;
  let memory: BrowserSession | null = null;
  let storageFailed = false;
  function read(time: number): BrowserSession | null {
    let value: unknown = memory;
    if (!storageFailed) {
      try {
        const raw = options.storage.read();
        value = raw ? JSON.parse(raw) : null;
      } catch {
        storageFailed = true;
      }
    }
    if (!value || typeof value !== "object") return null;
    const item = value as BrowserSession;
    if (
      typeof item.id !== "string" ||
      uuidV7Timestamp(item.id) !== item.startedAt ||
      !Number.isFinite(item.lastActivityAt) ||
      item.lastActivityAt < item.startedAt ||
      item.lastActivityAt > time ||
      item.startedAt > time ||
      time - item.lastActivityAt >= SESSION_IDLE_MS ||
      time - item.startedAt >= SESSION_MAX_MS
    )
      return null;
    return item;
  }
  function persist(value: BrowserSession | null) {
    memory = value;
    try {
      const raw = value ? JSON.stringify(value) : null;
      options.storage.write(raw);
      storageFailed = options.storage.read() !== raw;
    } catch {
      storageFailed = true;
    }
  }
  return {
    /** Read a live session without treating a passive request as user activity. */
    current(): BrowserSession | null {
      return read(now());
    },
    snapshot(): BrowserSession {
      const time = now();
      const previous = read(time);
      const next = Object.freeze({
        id: previous?.id ?? uuidV7(time),
        startedAt: previous?.startedAt ?? time,
        lastActivityAt: time,
      });
      persist(next);
      return next;
    },
    reset() {
      persist(null);
    },
  };
}
